// Shifts, staffing templates, availability, check-in and the hours they
// produce (spec §14, §34; ADR 0017). Hub owns shift scheduling for staff and
// volunteers. Booking's staffing screens are retired (ADR 0024): its
// upcoming shifts were imported here, its past records stay in Booking, and
// Hub no longer shows Booking's staffing summary on the Event page.
//
// Times. A shift starts and ends at a venue-local (America/Chicago) date and
// time, YYYY-MM-DDTHH:MM, the way incident times are kept, so a 7:00 PM
// shift is 7:00 PM whatever the season. Check-in and check-out are moments
// (ISO 8601 UTC), like every other recorded time.
//
// Not payroll. A shift records when someone was there, never what they are
// paid: no pay rate, no pay status, no paid date. A staff shift can be
// marked paid (ADR 0024); its attended hours, once a manager approves them,
// go on the payroll hours export (shifts.payroll_export) and payroll does
// the rest. A volunteer shift's attended time becomes a volunteer hours
// entry (ADR 0016) waiting for approval.

import { z } from 'zod'

const ID = z.string().min(1).max(100)
const text = (max: number) => z.string().trim().max(max)
const optionalText = (max: number) => text(max).nullable().optional()
const DAY = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
const LOCAL = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, 'Use YYYY-MM-DDTHH:MM')
const CLOCK = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM')

export const SHIFTS_SYSTEM_SUB = 'system:shifts'

export const NOT_PAYROLL_NOTE = 'Attendance only. Approved hours of paid staff shifts are exported for payroll.'
export const BOOKING_STAFFING_NOTE = 'Past Booking staffing stays in Booking, read-only.'

export const SHIFT_KINDS = ['volunteer', 'staff'] as const
export type ShiftKind = (typeof SHIFT_KINDS)[number]
export const SHIFT_KIND_LABELS: Record<ShiftKind, string> = { volunteer: 'Volunteer', staff: 'Staff' }

export const SHIFT_STATUSES = ['draft', 'published', 'canceled'] as const
export type ShiftStatus = (typeof SHIFT_STATUSES)[number]
export const SHIFT_STATUS_LABELS: Record<ShiftStatus, string> = { draft: 'Draft', published: 'Published', canceled: 'Canceled' }

// Why an assignment needs a second look. A manager may assign anyway with a
// reason (audited); a volunteer signing up never can.
export const ASSIGNMENT_WARNINGS = ['overlap', 'qualification_missing', 'qualification_expired', 'training_missing', 'training_lapsed', 'not_active', 'unavailable', 'full'] as const
export type AssignmentWarningCode = (typeof ASSIGNMENT_WARNINGS)[number]
export type AssignmentWarning = { code: AssignmentWarningCode; message: string }

export const ATTENDANCE_STATES = ['scheduled', 'checked_in', 'checked_out', 'no_show'] as const
export type AttendanceState = (typeof ATTENDANCE_STATES)[number]
export const ATTENDANCE_LABELS: Record<AttendanceState, string> = { scheduled: 'Not checked in', checked_in: 'Checked in', checked_out: 'Checked out', no_show: 'No-show' }

// Defaults for the settings shifts.manage can change.
export const DEFAULT_DROP_CUTOFF_HOURS = 24
export const DEFAULT_CHECKIN_WINDOW_MINUTES = 30
// A reminder goes out the day before a shift, from this local hour on.
export const SHIFT_REMINDER_HOUR = 9

// ------------------------------------------------------------- local time

// Minutes added to a venue-local YYYY-MM-DDTHH:MM, as wall-clock arithmetic.
export function addLocalMinutes(local: string, minutes: number): string {
  return new Date(Date.parse(`${local}:00Z`) + minutes * 60_000).toISOString().slice(0, 16)
}

export function localMinutesBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}:00Z`) - Date.parse(`${from}:00Z`)) / 60_000)
}

export const overlaps = (a: { startsLocal: string; endsLocal: string }, b: { startsLocal: string; endsLocal: string }) => a.startsLocal < b.endsLocal && b.startsLocal < a.endsLocal

// "7:00 PM", from a local time or HH:MM.
export function clockLabel(localOrClock: string): string {
  const hhmm = localOrClock.length > 5 ? localOrClock.slice(11, 16) : localOrClock
  const [h, m] = hhmm.split(':').map(Number) as [number, number]
  const suffix = h < 12 ? 'AM' : 'PM'
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${suffix}`
}

export function shiftTimeLabel(s: { startsLocal: string; endsLocal: string }): string {
  return `${clockLabel(s.startsLocal)} – ${clockLabel(s.endsLocal)}${s.endsLocal.slice(0, 10) !== s.startsLocal.slice(0, 10) ? ' (next day)' : ''}`
}

// ------------------------------------------------------------------ shapes

export type ShiftRequirementRef = { id: string; name: string }

export type ShiftAssignee = {
  assignmentId: string
  personId: string
  displayName: string
  source: 'manager' | 'signup'
  attendance: AttendanceState
  checkedInAt: string | null
  checkedOutAt: string | null
  attendedMinutes: number | null
  // Warnings when they were assigned, and the manager's reason for going ahead.
  warnings: AssignmentWarning[]
  overrideReason: string | null
  hoursEntryId: string | null
  // Paid staff shifts: when a manager approved the attended time for payroll.
  hoursApprovedAt: string | null
  // Only with workforce.sensitive, on the check-in page; absent otherwise.
  emergencyContact?: { name: string | null; phone: string | null; relationship: string | null }
}

export type Shift = {
  id: string
  eventId: string | null
  eventName: string | null
  // The event's first day (YYYY-MM-DD).
  eventStartsOn: string | null
  facilityId: string | null
  facilityName: string | null
  spaceId: string | null
  spaceName: string | null
  departmentId: string | null
  departmentName: string | null
  role: string
  kind: ShiftKind
  startsLocal: string
  endsLocal: string
  neededCount: number
  filledCount: number
  leadPersonId: string | null
  leadName: string | null
  // A staff shift whose attended hours go to payroll (hours only).
  paid: boolean
  // Imported from Booking (ADR 0024): where from, and why it needs a look.
  importedFrom: string | null
  importFlag: string | null
  requiredQualifications: ShiftRequirementRef[]
  requiredTraining: ShiftRequirementRef[]
  notes: string | null
  status: ShiftStatus
  cancelReason: string | null
  templateName: string | null
  version: number
  // Present when the viewer may see who is on it.
  assignees?: ShiftAssignee[]
}

// A shift as the person on it sees it (My shifts) or as a volunteer browsing
// open shifts sees it: never anyone else's name.
export type MyShift = Omit<Shift, 'assignees'> & {
  assignmentId: string | null
  attendance: AttendanceState | null
  checkedInAt: string | null
  checkedOutAt: string | null
  canSelfCheckIn: boolean
  canSelfCheckOut: boolean
  canDrop: boolean
  dropCutoff: string | null
  // Open shifts only: why you can't sign up, if you can't.
  blockers?: AssignmentWarning[]
}

export type StaffingTemplateLine = {
  key: string
  role: string
  departmentId: string | null
  kind: ShiftKind
  anchor: 'doors' | 'start'
  // Minutes from doors or start; negative is before.
  offsetMinutes: number
  durationMinutes: number
  neededCount: number
  qualificationTypeIds: string[]
  trainingItemIds: string[]
  notes: string | null
}

export type StaffingTemplate = {
  id: string
  name: string
  // What kind of event it is for ("Concert", "Festival"). Hub events have no
  // type of their own, so this is a label that helps people choose.
  eventKind: string | null
  departmentId: string | null
  departmentName: string | null
  notes: string | null
  lines: StaffingTemplateLine[]
  retiredAt: string | null
  version: number
}

export type ShiftSettings = { dropCutoffHours: number; checkinWindowMinutes: number }

export type Unavailability = { id: string; startsLocal: string; endsLocal: string; note: string | null; createdAt: string }
// What a manager sees: dates, never the person's note.
export type PersonUnavailability = { id: string; startsLocal: string; endsLocal: string }
export type UnavailablePerson = { personId: string; displayName: string; startsLocal: string; endsLocal: string }

// digest: the Monday digest of open shifts (ADR 0076), when this run made it.
export type ShiftsRunResult = { reminders: number; errors: string[]; digest?: { week: string; volunteers: number; emails: number; notices: number } }

// ------------------------------------------------------------------ bodies

const ShiftFields = {
  eventId: ID.nullable().optional(),
  facilityId: ID.nullable().optional(),
  spaceId: ID.nullable().optional(),
  departmentId: ID.nullable().optional(),
  role: text(120).min(1),
  kind: z.enum(SHIFT_KINDS),
  startsLocal: LOCAL,
  endsLocal: LOCAL,
  neededCount: z.number().int().min(1).max(500),
  leadPersonId: ID.nullable().optional(),
  qualificationTypeIds: z.array(ID).max(20).default([]),
  trainingItemIds: z.array(ID).max(20).default([]),
  notes: optionalText(2000),
  // Staff shifts only (ADR 0024).
  paid: z.boolean().optional(),
}

const timesInOrder = (b: { startsLocal?: string; endsLocal?: string }) => !b.startsLocal || !b.endsLocal || (b.endsLocal > b.startsLocal && localMinutesBetween(b.startsLocal, b.endsLocal) <= 24 * 60)
const TIMES_MESSAGE = { message: 'A shift must end after it starts, within 24 hours', path: ['endsLocal'] }

export const PAID_STAFF_ONLY = 'Only a staff shift can be paid'
export const ShiftBody = z
  .object({ ...ShiftFields, publish: z.boolean().optional() })
  .refine(timesInOrder, TIMES_MESSAGE)
  .refine((b) => !b.paid || b.kind === 'staff', { message: PAID_STAFF_ONLY, path: ['paid'] })
export const ShiftPatch = z
  .object({
    eventId: ShiftFields.eventId,
    facilityId: ShiftFields.facilityId,
    spaceId: ShiftFields.spaceId,
    departmentId: ShiftFields.departmentId,
    role: ShiftFields.role.optional(),
    kind: ShiftFields.kind.optional(),
    startsLocal: LOCAL.optional(),
    endsLocal: LOCAL.optional(),
    neededCount: ShiftFields.neededCount.optional(),
    leadPersonId: ShiftFields.leadPersonId,
    qualificationTypeIds: z.array(ID).max(20).optional(),
    trainingItemIds: z.array(ID).max(20).optional(),
    notes: ShiftFields.notes,
    paid: z.boolean().optional(),
    expectedVersion: z.number().int().min(1),
  })
  .refine(timesInOrder, TIMES_MESSAGE)
export const ShiftCancelBody = z.object({ reason: text(500).min(3), expectedVersion: z.number().int().min(1) })
export const ShiftPublishBody = z.object({ expectedVersion: z.number().int().min(1) })

export const AssignBody = z.object({
  personId: ID,
  // Needed when the assignment has warnings; recorded in the audit line.
  overrideReason: text(500).min(3).optional(),
})
export const RemoveAssignmentBody = z.object({ reason: optionalText(500) })

export const UnavailabilityBody = z
  .object({ startsLocal: LOCAL, endsLocal: LOCAL, note: optionalText(300) })
  .refine((b) => b.endsLocal > b.startsLocal && localMinutesBetween(b.startsLocal, b.endsLocal) <= 60 * 24 * 60, { message: 'It must end after it starts, within 60 days', path: ['endsLocal'] })

export const ShiftSettingsBody = z.object({
  dropCutoffHours: z.number().int().min(0).max(24 * 14),
  checkinWindowMinutes: z.number().int().min(5).max(240),
})

const TemplateLineBody = z.object({
  key: z.string().regex(/^[a-z0-9_-]{1,40}$/).optional(),
  role: text(120).min(1),
  departmentId: ID.nullable().optional(),
  kind: z.enum(SHIFT_KINDS),
  anchor: z.enum(['doors', 'start']),
  offsetMinutes: z.number().int().min(-24 * 60).max(24 * 60),
  durationMinutes: z.number().int().min(15).max(24 * 60),
  neededCount: z.number().int().min(1).max(500),
  qualificationTypeIds: z.array(ID).max(20).default([]),
  trainingItemIds: z.array(ID).max(20).default([]),
  notes: optionalText(1000),
})

export const StaffingTemplateBody = z.object({
  name: text(120).min(1),
  eventKind: optionalText(60),
  departmentId: ID.nullable().optional(),
  notes: optionalText(2000),
  lines: z.array(TemplateLineBody).min(1).max(60),
})
export const StaffingTemplatePatch = StaffingTemplateBody.partial().extend({ retired: z.boolean().optional(), expectedVersion: z.number().int().min(1) })

// Make an event's shifts from a template, for one day of the event. Hub
// events carry dates, not doors or show times, so they are given here.
export const GenerateShiftsBody = z.object({
  templateId: ID,
  day: DAY,
  doors: CLOCK,
  start: CLOCK,
  facilityId: ID.nullable().optional(),
  spaceId: ID.nullable().optional(),
})

// ---------------------------------------------------- payroll hours export
//
// Paid staff shifts' attended time, for payroll (ADR 0024). Hours only: who,
// when, which shift, checked in and out, attended minutes and hours, and who
// approved them. Never a rate, an amount, a pay status or a paid date.
export const PAYROLL_EXPORT_COLUMNS = ['Person', 'Date', 'Shift', 'Event', 'Checked in', 'Checked out', 'Attended minutes', 'Attended hours', 'Approved by', 'Approved at'] as const
export const PayrollExportQuery = z.object({ from: DAY, to: DAY, approved: z.enum(['all', 'approved']).optional() })
